import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormNumberField from '../../../components/ui/form/FormNumberField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { criteriaScoreFormSchema, type CriteriaScoreFormValues } from './criteriaScoreFormSchema'
import type { CriteriaScore, CriteriaScoreUpsert } from '../types'

interface Props {
  visible: boolean
  initial: CriteriaScore | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: CriteriaScoreUpsert) => void
  onHide: () => void
}

/**
 * Değerlendirme kriteri ekle/düzenle formu (028: react-hook-form + zod). TR ad zorunlu; EN opsiyonel;
 * ağırlık zorunlu (0–100). Sıra formda yok. (025 InvitationTypeFormDialog deseni + ağırlık alanı.)
 */
export default function CriteriaScoreFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(criteriaScoreFormSchema, {
    defaultValues: { nameTr: '', nameEn: '', weight: null },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
        weight: initial?.weight ?? null,
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: CriteriaScoreFormValues) =>
    onSubmit({
      nameTr: v.nameTr,
      nameEn: v.nameEn ? v.nameEn : null,
      // superRefine, gönderimde weight'in null olmamasını garanti eder.
      weight: v.weight as number,
    })

  return (
    <FormDialog
      visible={visible}
      header={
        initial
          ? t('activity.criteriaScores.form.editTitle')
          : t('activity.criteriaScores.form.newTitle')
      }
      submitLabel={t('activity.criteriaScores.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.criteriaScores.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.criteriaScores.form.nameEn')}
        />
        <FormNumberField
          control={control}
          name="weight"
          label={t('activity.criteriaScores.columns.weight')}
          required
          min={0}
        />
      </div>
    </FormDialog>
  )
}
